// Kana Capsules (reading): katakana one sound at a time, then real words from shelves, signs
// and bottles once their kana have been seen, then sign questions once 20 kana are learned.
import type { Card, Pack } from '../../../../shared/pack/schema'
import { basicKanaIds, scriptDecks } from '../../kana'
import { cardsOf, choices, learnDecks, lookup, sceneFor, shortMeaning } from '../cards'
import { isLearned } from '../srs'
import type { ChoiceItem, CardSet, CardSetCtx } from '../types'
import { t } from '../../../strings/en'

/** The word decks: the kit's decks marked `learn: ["read"]` (B7; shelf, snow and onsen words for Hokkaido). */
const wordDecks = (pack: Pack) => learnDecks(pack, 'read').map((d) => d.id)
/** Sign/culture questions open up after this many basic kana are learned. */
const MCQ_AFTER_LEARNED = 20

/** The first script (katakana for Japanese, hangul for Korean, from the kit): the capsule's characters (WI-10). */
const firstScript = (pack: Pack) => scriptDecks(pack)[0] ?? ''

/**
 * A word's letters as the first script's deck teaches them: kana as they are; a Hangul syllable
 * block as its jamo (가 → ㄱ ㅏ), since a Hangul deck teaches letters.
 */
export function lettersOf(word: string): string[] {
  return [...word].flatMap((ch) => {
    const s = ch.codePointAt(0)! - 0xac00
    if (s < 0 || s > 11171) return [ch]
    const lead = LEAD[Math.floor(s / 588)]!
    const vowel = VOWEL[Math.floor((s % 588) / 28)]!
    const tail = TAIL[s % 28]!
    return [lead, ...[...vowel], ...(tail ? [...tail] : [])]
  })
}
// Hangul syllable = lead × 588 + vowel × 28 + tail, as compatibility jamo; compound vowels and
// tails split into the basic letters a child meets first (ㅘ → ㅗ ㅏ, ㄳ → ㄱ ㅅ).
const LEAD = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ']
const VOWEL = ['ㅏ', 'ㅐ', 'ㅑ', 'ㅒ', 'ㅓ', 'ㅔ', 'ㅕ', 'ㅖ', 'ㅗ', 'ㅗㅏ', 'ㅗㅐ', 'ㅗㅣ', 'ㅛ', 'ㅜ', 'ㅜㅓ', 'ㅜㅔ', 'ㅜㅣ', 'ㅠ', 'ㅡ', 'ㅡㅣ', 'ㅣ']
const TAIL = ['', 'ㄱ', 'ㄲ', 'ㄱㅅ', 'ㄴ', 'ㄴㅈ', 'ㄴㅎ', 'ㄷ', 'ㄹ', 'ㄹㄱ', 'ㄹㅁ', 'ㄹㅂ', 'ㄹㅅ', 'ㄹㅌ', 'ㄹㅍ', 'ㄹㅎ', 'ㅁ', 'ㅂ', 'ㅂㅅ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ']

/**
 * Every letter of `script` that the first script's deck teaches has been seen (its card is in
 * box ≥ 1). Letters the deck doesn't teach (ー, punctuation, kanji) don't hold a word back.
 */
function kanaSeen(ctx: CardSetCtx, script: string): boolean {
  const taught = new Set<string>()
  const seen = new Set<string>()
  for (const c of cardsOf(ctx.pack, firstScript(ctx.pack))) {
    if (c.kind !== 'reading') continue
    for (const ch of c.script) taught.add(ch)
    if ((ctx.progress.cards[c.id]?.box ?? 0) > 0) for (const ch of c.script) seen.add(ch)
  }
  return lettersOf(script).filter((ch) => taught.has(ch)).every((ch) => seen.has(ch))
}

function learnedBasics(ctx: CardSetCtx): number {
  return basicKanaIds(ctx.pack).filter((id) => isLearned(ctx.progress.cards[id])).length
}

export const kanaCapsules: CardSet = {
  id: 'kana-capsules',
  kind: 'reading',
  title: 'Kana Capsules',
  blurb: t.arcade.blurbs['kana-capsules']!,
  theme: { glyph: '', jpGlyph: true, color: 'red' },
  sessionSize: 5,
  newPerSession: 2,

  pool(pack: Pack) {
    const kana = cardsOf(pack, firstScript(pack)).map((c) => c.id)
    const words = wordDecks(pack).flatMap((d) => cardsOf(pack, d).filter((c) => c.kind === 'reading').map((c) => c.id))
    const mcqs = wordDecks(pack).flatMap((d) => cardsOf(pack, d).filter((c) => c.kind === 'mcq').map((c) => c.id))
    return [...kana, ...words, ...mcqs]
  },

  eligible(ctx, id) {
    const hit = lookup(ctx.pack, id)
    if (!hit) return false
    if (hit.deck.id === firstScript(ctx.pack)) return true
    if (hit.card.kind === 'reading') return kanaSeen(ctx, hit.card.script)
    if (hit.card.kind === 'mcq') return learnedBasics(ctx) >= MCQ_AFTER_LEARNED
    return false
  },


  toItem(ctx, id, rand): ChoiceItem | null {
    const hit = lookup(ctx.pack, id)
    if (!hit) return null
    const { card, deck } = hit
    const scene = sceneFor(card, deck)
    if (card.kind === 'mcq') {
      const right = card.options[card.answer]!
      const c = choices(right, card.options, [], rand, card.options.length)
      return {
        type: 'choice',
        cardId: id,
        ...scene,
        text: card.prompt,
        question: '',
        options: c.options,
        answer: c.answer,
        reveal: { line: right, ...(card.anecdote ? { note: card.anecdote } : {}), ...(card.say ? { say: card.say } : {}) },
        xp: card.xp,
      }
    }
    if (card.kind !== 'reading') return null
    const say = card.say ?? card.script
    if (scriptDecks(ctx.pack).includes(deck.id)) {
      const readings = (cards: Card[]) => cards.flatMap((c) => (c.kind === 'reading' ? [c.reading] : []))
      const all = cardsOf(ctx.pack, deck.id)
      const group = (deck.lookalikes ?? []).find((g) => g.includes(card.script)) ?? []
      const near = readings(all.filter((c) => c.kind === 'reading' && group.includes(c.script)))
      const sameLen = readings(all.filter((c) => c.kind === 'reading' && [...c.script].length === [...card.script].length))
      const c = choices(card.reading, near, sameLen, rand)
      return {
        type: 'choice',
        cardId: id,
        // A script card shows no context line, but its example word's picture when it has one.
        ...(scene.art ? { art: scene.art } : {}),
        big: card.script,
        question: 'Which sound is this?',
        options: c.options,
        answer: c.answer,
        // The deck's meaning line carries the example word ("ka — as in カード (card)").
        reveal: { big: card.script, line: card.reading, note: card.anecdote ?? card.meaning, say, example: card.meaning },
        xp: card.xp,
      }
    }
    // A word: what does it say?
    const words = wordDecks(ctx.pack).flatMap((d) => cardsOf(ctx.pack, d)).filter((c): c is Extract<Card, { kind: 'reading' }> => c.kind === 'reading' && c.id !== id)
    const right = shortMeaning(card.meaning)
    // Near: words sharing a tag, other than the script's own (every word is tagged katakana).
    const scripts = scriptDecks(ctx.pack)
    const near = words.filter((w) => w.tags.some((t) => card.tags.includes(t) && !scripts.includes(t))).map((w) => shortMeaning(w.meaning))
    const c = choices(right, near, words.map((w) => shortMeaning(w.meaning)), rand)
    return {
      type: 'choice',
      cardId: id,
      ...scene,
      big: card.script,
      question: 'What does it say?',
      options: c.options,
      answer: c.answer,
      reveal: { big: card.script, line: `${card.reading} · ${right}`, ...(card.anecdote ? { note: card.anecdote } : {}), say },
      xp: card.xp,
    }
  },

  // The same count as Learn's track for this script (WI-10, L2-03): learned of the whole deck,
  // titled by the deck ("Katakana 3 / 91"); it used to count the 46 basics only.
  progress(ctx) {
    const deck = ctx.pack.decks.find((d) => d.id === firstScript(ctx.pack))
    if (!deck) return null
    return { label: deck.title, value: deck.cards.filter((c) => isLearned(ctx.progress.cards[c.id])).length, max: deck.cards.length }
  },
}
